// The setter playbook (content/SETTER-PLAYBOOK.md, bundled as text by Vite's ?raw import) on the Training page:
// a section list on the left, the playbook on the right, deep links as /training/playbook/<section-id>.
// Rendered from parsed data (markdown.ts) as elements: nothing from the file is injected as HTML.
import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import playbookSrc from '../../../content/SETTER-PLAYBOOK.md?raw';
import { navigate } from '../app';
import { type Block, type Inline, parseBlocks, safeHref, sectionsOf } from './markdown';

export const PLAYBOOK_BLOCKS: Block[] = parseBlocks(playbookSrc);
export const PLAYBOOK_SECTIONS = sectionsOf(PLAYBOOK_BLOCKS);

export const playbookPath = (section: string | null) => (section ? `/training/playbook/${section}` : '/training/playbook');
const anchor = (id: string) => `pb-${id}`;

function scrollToSection(section: string | null): void {
  if (!section) { window.scrollTo({ top: 0 }); return; }
  document.getElementById(anchor(section))?.scrollIntoView({ block: 'start' });
}

function Inl(props: { c: Inline[] }) {
  return <>{props.c.map((i) => inline(i))}</>;
}

function inline(i: Inline): ComponentChildren {
  switch (i.t) {
    case 'text': return i.v;
    case 'code': return <code>{i.v}</code>;
    case 'strong': return <strong><Inl c={i.c} /></strong>;
    case 'em': return <em><Inl c={i.c} /></em>;
    case 'link': {
      const href = safeHref(i.href);
      if (!href) return <Inl c={i.c} />;
      return href.startsWith('#') ? <a href={href}><Inl c={i.c} /></a> : <a href={href} target="_blank" rel="noopener noreferrer"><Inl c={i.c} /></a>;
    }
  }
}

function BlockView(props: { b: Block }): ComponentChildren {
  const b = props.b;
  switch (b.t) {
    case 'heading': {
      // The page already has an h1 ("Training"): the playbook title is an h2, its sections h3, and so on.
      if (b.level <= 1) return <h2 class="tr-md-title"><Inl c={b.c} /></h2>;
      if (b.level === 2) return <h3 id={anchor(b.id)}><Inl c={b.c} /></h3>;
      return <h4 id={anchor(b.id)}><Inl c={b.c} /></h4>;
    }
    case 'para': return <p><Inl c={b.c} /></p>;
    case 'hr': return <hr />;
    case 'code': return <pre class="tr-pre">{b.v}</pre>;
    case 'quote': return <blockquote>{b.c.map((x) => <BlockView b={x} />)}</blockquote>;
    case 'table': return (
      <div class="tr-md-table">
        <table>
          <thead><tr>{b.head.map((h) => <th><Inl c={h} /></th>)}</tr></thead>
          <tbody>{b.rows.map((r) => <tr>{r.map((cell) => <td><Inl c={cell} /></td>)}</tr>)}</tbody>
        </table>
      </div>
    );
    case 'list': {
      const items = b.items.map((it) => <li><Inl c={it.c} />{it.children.map((x) => <BlockView b={x} />)}</li>);
      return b.ordered ? <ol start={b.start}>{items}</ol> : <ul>{items}</ul>;
    }
  }
}

export function Playbook(props: { section: string | null }) {
  useEffect(() => { scrollToSection(props.section); }, [props.section]);

  const go = (e: Event, section: string | null) => {
    e.preventDefault();
    navigate(playbookPath(section));
    scrollToSection(section); // also when the same section is clicked again
  };

  return (
    <div class="tr-pb">
      <nav class="tr-pb-toc" aria-label="Playbook sections">
        <a href={playbookPath(null)} class={props.section ? '' : 'active'} onClick={(e) => go(e, null)}>Top</a>
        {PLAYBOOK_SECTIONS.map((s) => (
          <a href={playbookPath(s.id)} class={props.section === s.id ? 'active' : ''} aria-current={props.section === s.id ? 'location' : undefined}
            onClick={(e) => go(e, s.id)}>{s.num ? `${s.num}. ` : ''}{s.title}</a>
        ))}
      </nav>
      <article class="card tr-md">
        {PLAYBOOK_BLOCKS.map((b) => <BlockView b={b} />)}
      </article>
    </div>
  );
}
